import React, { useState, useEffect } from 'react';
import './UserManager.css';
import { getAllUsers, addUser, updateUserRole, deleteUser, toggleUserStatus } from '../../firebase/userService';

function UserManager({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'editor'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processing, setProcessing] = useState(false);

  // إخفاء رسائل النجاح والخطأ تلقائياً بعد 5 ثوانٍ
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  const [showPassword, setShowPassword] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const allUsers = await getAllUsers();
      
      // إذا لم يكن هناك مستخدمين، أضف المستخدم الحالي كمدير وحفظه في Firestore
      if (allUsers.length === 0 && currentUser) {
        const currentUserData = {
          id: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || 'المدير',
          role: 'admin',
          createdAt: new Date().toISOString(),
          isSystemAdmin: true // علامة للمدير الأساسي
        };
        
        // حفظ المستخدم الحالي في Firestore
        try {
          const { doc, setDoc } = await import('firebase/firestore');
          const { db } = await import('../../firebase/config');
          await setDoc(doc(db, 'users', currentUser.uid), {
            email: currentUser.email,
            displayName: currentUser.displayName || 'المدير',
            role: 'admin',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            active: true,
            isSystemAdmin: true
          });
        } catch (saveError) {
          console.error('Error saving current user:', saveError);
        }
        
        setUsers([currentUserData]);
      } else {
        setUsers(allUsers);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setError('فشل تحميل المستخدمين');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setProcessing(true);

    try {
      // التحقق من البيانات
      if (!newUser.email || !newUser.password || !newUser.displayName) {
        throw new Error('جميع الحقول مطلوبة');
      }

      if (newUser.password.length < 6) {
        throw new Error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      }

      // محاولة استخدام Cloud Function أولاً
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const createUser = httpsCallable(functions, 'createUser');
        
        await createUser({
          email: newUser.email,
          password: newUser.password,
          displayName: newUser.displayName,
          role: newUser.role
        });
        
        setSuccess('تم إضافة المستخدم بنجاح');
        setShowAddModal(false);
        setNewUser({
          email: '',
          password: '',
          displayName: '',
          role: 'editor'
        });
        loadUsers();
      } catch (cloudError) {
        // إذا فشلت Cloud Function، استخدم الطريقة القديمة
        console.log('Cloud Function not available, using fallback method');
        await addUser(newUser);
        setSuccess('تم إضافة المستخدم بنجاح - سيتم إعادة تحميل الصفحة');
        setShowAddModal(false);
        setNewUser({
          email: '',
          password: '',
          displayName: '',
          role: 'editor'
        });
        
        // إعادة تحميل الصفحة بعد ثانيتين
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      console.error('Error adding user:', error);
      setError(error.message || 'فشل إضافة المستخدم');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      setSuccess('تم تحديث الصلاحيات بنجاح');
      loadUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      setError('فشل تحديث الصلاحيات');
    }
  };

  const handleToggleUserStatus = async (userId, userEmail, currentStatus) => {
    // المدير فقط محمي من التعطيل
    if (userEmail === 'admin@salman-alkhatep.com') {
      setError('لا يمكن تعطيل المدير الأساسي');
      return;
    }

    if (userEmail === currentUser.email) {
      setError('لا يمكنك تعطيل حسابك الخاص');
      return;
    }

    try {
      const newStatus = await toggleUserStatus(userId, currentStatus);
      setSuccess(newStatus ? 'تم تفعيل المستخدم بنجاح' : 'تم تعطيل المستخدم بنجاح');
      loadUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      setError('فشل تغيير حالة المستخدم');
    }
  };

  const handleDeleteUser = async (userId, userEmail, isSystemAdmin) => {
    if (userEmail === currentUser.email) {
      setError('لا يمكنك حذف حسابك الخاص');
      return;
    }

    if (isSystemAdmin) {
      setError('لا يمكن حذف المدير الأساسي للنظام');
      return;
    }

    // قائمة المستخدمين الثابتين المحميين
    const protectedUsers = [
      'admin@salman-alkhatep.com',
      'editor@salman-alkhatep.com',
      'designer@salman-alkhatep.com'
    ];

    if (protectedUsers.includes(userEmail)) {
      setError('هذا المستخدم ثابت في النظام ولا يمكن حذفه');
      return;
    }

    // فتح نافذة التأكيد
    setUserToDelete({ userId, userEmail });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    setProcessing(true);
    try {
      await deleteUser(userToDelete.userId);
      setSuccess('تم حذف المستخدم بنجاح');
      setShowDeleteModal(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('فشل حذف المستخدم');
    } finally {
      setProcessing(false);
    }
  };

  const handleEditUser = (user) => {
    // قائمة المستخدمين الثابتين المحميين
    const protectedUsers = [
      'admin@salman-alkhatep.com',
      'editor@salman-alkhatep.com',
      'designer@salman-alkhatep.com'
    ];

    if (protectedUsers.includes(user.email)) {
      setError('هذا المستخدم ثابت في النظام ولا يمكن تعديل بياناته');
      return;
    }

    setUserToEdit({
      id: user.id,
      email: user.email,
      displayName: user.displayName,
      role: user.role,
      newPassword: '',
      confirmPassword: ''
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setProcessing(true);

    try {
      if (!userToEdit.displayName) {
        throw new Error('الاسم الكامل مطلوب');
      }

      if (!userToEdit.email) {
        throw new Error('البريد الإلكتروني مطلوب');
      }

      // التحقق من كلمة المرور إذا تم إدخالها
      if (userToEdit.newPassword) {
        if (userToEdit.newPassword.length < 6) {
          throw new Error('كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل');
        }
        if (userToEdit.newPassword !== userToEdit.confirmPassword) {
          throw new Error('كلمة المرور الجديدة وتأكيدها غير متطابقين');
        }
      }

      const { updateUserComplete } = await import('../../firebase/userService');
      await updateUserComplete(userToEdit.id, {
        email: userToEdit.email,
        displayName: userToEdit.displayName,
        role: userToEdit.role,
        newPassword: userToEdit.newPassword || null
      });

      setSuccess('تم تحديث المستخدم بنجاح');
      setShowEditModal(false);
      setUserToEdit(null);
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error.message || 'فشل تحديث المستخدم');
    } finally {
      setProcessing(false);
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      admin: 'مدير',
      editor: 'محرر',
      viewer: 'مصمم'
    };
    return roles[role] || role;
  };

  const getRoleDescription = (role) => {
    const descriptions = {
      admin: 'الوصول لجميع الصفحات',
      editor: 'إدارة التصاميم والتصنيفات فقط',
      viewer: 'إضافة تصاميم فقط'
    };
    return descriptions[role] || '';
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner-large"></div>
        <p>جاري تحميل المستخدمين...</p>
      </div>
    );
  }

  return (
    <div className="user-manager">
      {error && (
        <div className="alert alert-error">
          <span>❌</span>
          <span>{error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>✅</span>
          <span>{success}</span>
          <button onClick={() => setSuccess('')}>✕</button>
        </div>
      )}

      <div className="user-manager-header">
        <div className="header-info">
          <h2>إدارة المستخدمين</h2>
          <p>إضافة وإدارة المستخدمين وصلاحياتهم</p>
        </div>
        <button className="btn-primary" onClick={() => {
          setShowAddModal(true);
          setNewUser({
            email: '',
            password: '',
            displayName: '',
            role: 'editor'
          });
          setShowPassword(false);
          setError('');
          setSuccess('');
        }}>
          <span>➕</span>
          إضافة مستخدم جديد
        </button>
      </div>

      <div className="users-list">
        {users.length === 0 ? (
          <div className="empty-state">
            <p>لا يوجد مستخدمين بعد</p>
          </div>
        ) : (
          users.map((user) => (
            <div key={user.id} className="user-card">
              <div className="user-avatar">
                <span>{user.displayName?.charAt(0) || user.email.charAt(0)}</span>
              </div>
              <div className="user-info">
                <h3>{user.displayName || 'بدون اسم'}</h3>
                <p className="user-email">{user.email}</p>
                <span className={`role-badge role-${user.role}`}>
                  {getRoleLabel(user.role)}
                </span>
                <span className="user-permissions">
                  {getRoleDescription(user.role)}
                </span>
                <span className="user-date">
                  انضم: {new Date(user.createdAt).toLocaleDateString('ar-SA')}
                </span>
              </div>
              <div className="user-actions">
                {(() => {
                  const protectedUsers = [
                    'admin@salman-alkhatep.com',
                    'editor@salman-alkhatep.com',
                    'designer@salman-alkhatep.com'
                  ];
                  const isProtected = protectedUsers.includes(user.email);

                  if (user.email === currentUser.email) {
                    return (
                      <>
                        <span className="current-user-badge">أنت</span>
                      </>
                    );
                  }

                  if (user.isSystemAdmin && user.email !== currentUser.email) {
                    return <span className="system-admin-badge">مدير أساسي</span>;
                  }

                  // إظهار الأزرار لجميع المستخدمين (مع حماية الثابتين من التعطيل/التعديل/الحذف)
                  return (
                    <>
                      <button
                        className={`btn-toggle ${user.active !== false ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleUserStatus(user.id, user.email, user.active !== false)}
                        title={user.active !== false ? "تعطيل المستخدم" : "تفعيل المستخدم"}
                      >
                        {user.active !== false ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="8.5" cy="7" r="4"></circle>
                            <line x1="23" y1="11" x2="17" y2="11"></line>
                          </svg>
                        )}
                      </button>
                      {!isProtected && (
                        <>
                          <button
                            className="btn-edit"
                            onClick={() => handleEditUser(user)}
                            title="تعديل المستخدم"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDeleteUser(user.id, user.email, user.isSystemAdmin)}
                            title="حذف المستخدم"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </>
                      )}
                      {isProtected && (
                        <span className="protected-user-badge">مستخدم ثابت</span>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          ))
        )}
      </div>

      {/* نافذة إضافة مستخدم جديد */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>إضافة مستخدم جديد</h2>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>✕</button>
            </div>

            <form onSubmit={handleAddUser} className="add-user-form" key={showAddModal ? 'new' : 'closed'}>
              <div className="form-group">
                <label>الاسم الكامل *</label>
                <input
                  type="text"
                  value={newUser.displayName}
                  onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                  placeholder="مثال: أحمد محمد"
                  autoComplete="off"
                  required
                />
              </div>

              <div className="form-group">
                <label>البريد الإلكتروني *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="example@domain.com"
                  autoComplete="off"
                  required
                />
              </div>

              <div className="form-group">
                <label>كلمة المرور *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="6 أحرف على الأقل"
                    autoComplete="new-password"
                    minLength="6"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>الصلاحيات *</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  required
                >
                  <option value="admin">مدير - صلاحيات كاملة</option>
                  <option value="editor">محرر - إضافة وتعديل المحتوى</option>
                  <option value="viewer">مصمم - إضافة تصاميم فقط</option>
                </select>
                <p className="role-description">
                  {getRoleDescription(newUser.role)}
                </p>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowAddModal(false)}
                  disabled={processing}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={processing}
                >
                  {processing ? 'جاري الإضافة...' : 'إضافة المستخدم'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* معلومات الصلاحيات */}
      <div className="roles-info">
        <h3>شرح الصلاحيات - الوصول لصفحات القائمة الجانبية</h3>
        <p className="roles-description">الصلاحيات تحدد الصفحات التي يمكن للمستخدم الوصول إليها في القائمة الجانبية</p>
        <div className="roles-grid">
          <div className="role-info-card">
            <div className="role-icon admin">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <h4>مدير (Admin)</h4>
            <p className="role-subtitle">الوصول لجميع الصفحات</p>
            <ul>
              <li>✅ الإحصائيات</li>
              <li>✅ إدارة التصاميم</li>
              <li>✅ إضافة تصميم جديد</li>
              <li>✅ إدارة التصنيفات</li>
              <li>✅ إدارة صفحة التواصل</li>
              <li>✅ إدارة صفحة عن الشيخ</li>
              <li>✅ الرسائل</li>
              <li>✅ إدارة المستخدمين</li>
            </ul>
          </div>

          <div className="role-info-card">
            <div className="role-icon editor">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </div>
            <h4>محرر (Editor)</h4>
            <p className="role-subtitle">إدارة المحتوى فقط</p>
            <ul>
              <li>❌ الإحصائيات</li>
              <li>✅ إدارة التصاميم</li>
              <li>✅ إضافة تصميم جديد</li>
              <li>✅ إدارة التصنيفات</li>
              <li>❌ إدارة صفحة التواصل</li>
              <li>❌ إدارة صفحة عن الشيخ</li>
              <li>❌ الرسائل</li>
              <li>❌ إدارة المستخدمين</li>
            </ul>
          </div>

          <div className="role-info-card">
            <div className="role-icon viewer">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </div>
            <h4>مصمم (Designer)</h4>
            <p className="role-subtitle">إضافة تصاميم فقط</p>
            <ul>
              <li>❌ الإحصائيات</li>
              <li>❌ إدارة التصاميم</li>
              <li>✅ إضافة تصميم جديد</li>
              <li>❌ إدارة التصنيفات</li>
              <li>❌ إدارة صفحة التواصل</li>
              <li>❌ إدارة صفحة عن الشيخ</li>
              <li>❌ الرسائل</li>
              <li>❌ إدارة المستخدمين</li>
            </ul>
          </div>
        </div>
      </div>

      {/* نافذة تأكيد الحذف */}
      {showDeleteModal && userToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-icon">
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            
            <h2 className="delete-modal-title">تأكيد حذف المستخدم</h2>
            
            <div className="delete-modal-body">
              <p className="delete-warning">هل أنت متأكد من حذف المستخدم التالي؟</p>
              <div className="user-to-delete-info">
                <div className="user-to-delete-avatar">
                  <span>{userToDelete.userEmail.charAt(0)}</span>
                </div>
                <div>
                  <p className="user-to-delete-email">{userToDelete.userEmail}</p>
                  <p className="delete-note">⚠️ هذا الإجراء لا يمكن التراجع عنه</p>
                </div>
              </div>
            </div>

            <div className="delete-modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                }}
                disabled={processing}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="btn-confirm-delete"
                onClick={confirmDelete}
                disabled={processing}
              >
                {processing ? 'جاري الحذف...' : 'تأكيد الحذف'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* نافذة تعديل المستخدم */}
      {showEditModal && userToEdit && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>تعديل المستخدم</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>✕</button>
            </div>

            <form onSubmit={handleUpdateUser} className="add-user-form">
              <div className="form-group">
                <label>الاسم الكامل *</label>
                <input
                  type="text"
                  value={userToEdit.displayName}
                  onChange={(e) => setUserToEdit({ ...userToEdit, displayName: e.target.value })}
                  placeholder="مثال: أحمد محمد"
                  autoComplete="off"
                  required
                />
              </div>

              <div className="form-group">
                <label>البريد الإلكتروني</label>
                <input
                  type="email"
                  value={userToEdit.email}
                  disabled
                  style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                />
                <p style={{ fontSize: '12px', color: '#666', margin: '5px 0 0 0' }}>
                  لا يمكن تعديل البريد الإلكتروني
                </p>
              </div>

              <div className="form-group">
                <label>كلمة المرور الجديدة (اختياري)</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={userToEdit.newPassword}
                    onChange={(e) => setUserToEdit({ ...userToEdit, newPassword: e.target.value })}
                    placeholder="اتركه فارغاً إذا لم ترد التغيير"
                    autoComplete="new-password"
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>تأكيد كلمة المرور الجديدة</label>
                <input
                  type={showPassword ? "text" : "password"}
                  value={userToEdit.confirmPassword}
                  onChange={(e) => setUserToEdit({ ...userToEdit, confirmPassword: e.target.value })}
                  placeholder="أعد إدخال كلمة المرور الجديدة"
                  autoComplete="new-password"
                  minLength="6"
                />
              </div>

              <div className="form-group">
                <label>الصلاحيات *</label>
                <select
                  value={userToEdit.role}
                  onChange={(e) => setUserToEdit({ ...userToEdit, role: e.target.value })}
                  required
                >
                  <option value="admin">مدير - صلاحيات كاملة</option>
                  <option value="editor">محرر - إضافة وتعديل المحتوى</option>
                  <option value="viewer">مصمم - إضافة تصاميم فقط</option>
                </select>
                <p className="role-description">
                  {getRoleDescription(userToEdit.role)}
                </p>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowEditModal(false)}
                  disabled={processing}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={processing}
                >
                  {processing ? 'جاري التحديث...' : 'تحديث المستخدم'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManager;
